import { Observable } from 'rxjs';

import { AppSelectors } from './app.selectors';
import { Checklist } from '../../features/checklist/models/checklist.model';
import { DealFilter } from '../../features/deal-filter/models/deal-filter.model';
import { Formula } from '../../features/formula/models/formula.model';
import { Transaction } from '../../features/home/models/transaction.model';
import { Topic } from '../../features/market-comparison/models/topic.model';
import { SemerisRoutes } from '../../features/shared/constants/routes';
import { TruncatePipe } from '../../features/shared/pipes/truncate.pipe';
import { DatapointField } from '../../features/unified-datapoint/models/datapoint-field.models';
import {
  DatapointRule,
  DatapointRuleType,
  displayNameMapper,
} from '../../features/unified-datapoint/models/datapoint-rule.model';
import { ChecklistSelectors } from '../checklist/checklist.selectors';
import { createReferenceMemoizedSelector } from '../create-memoized-selector';
import { DatapointFieldSelectors } from '../datapoint-field/datapoint-field.selectors';
import { DealSelectors } from '../deal/deal.selectors';
import { DealComparisonSelectors } from '../deal-comparison/deal-comparison.selectors';
import { DealFilterSelectors } from '../deal-filter/deal-filter.selectors';
import { ExtractionRuleSelectors } from '../extraction-rule/extraction-rule.selectors';
import { FormulaSelectors } from '../formula/formula.selectors';
import { TopicSelectors } from '../topic/topic.selectors';

interface TitleParameters {
  url?: string;
  deals?: Transaction[];
  deal?: Transaction;
  dealFilter?: DealFilter;
  topic?: Topic;
  rule?: DatapointRule;
  field?: DatapointField;
  formula?: Formula;
  checklist?: Checklist;
  urlSearchParams?: URLSearchParams;
}

export namespace TitleSelectors {
  const TITLE_BASE = 'Semeris';

  const PREFIX = 'TitleSelectors';

  export const title = createReferenceMemoizedSelector(
    AppSelectors.url,
    DealComparisonSelectors.deals,
    TopicSelectors.topic,
    DealFilterSelectors.appliedFilter,
    DealSelectors.deal,
    ExtractionRuleSelectors.rule,
    DatapointFieldSelectors.datapointField,
    FormulaSelectors.selectedFormula,
    ChecklistSelectors.checklist,
    (url, deals, topic, dealFilter, deal, rule, field, formula, checklist) => {
      if (!url) {
        return TITLE_BASE;
      }

      const urlSearchParams: URLSearchParams = new URL(`${window.origin}${url}`).searchParams;

      return getTitle({
        url,
        deals,
        topic,
        dealFilter,
        deal,
        field,
        rule,
        formula,
        checklist,
        urlSearchParams,
      });
    },
    `${PREFIX}.title`
  );

  const simpleTitles: Record<
    string,
    string | ((input: TitleParameters) => Observable<string> | string)
  > = {
    [`(/[^/]+)?/$`]: `${TITLE_BASE} - Dashboard`,
    [`(/[^/]+)?/${SemerisRoutes.home.root}$`]: `${TITLE_BASE} - Dashboard`,
    [`(/[^/]+)?/${SemerisRoutes.search.documentSearch}.*`]: ({ urlSearchParams }) =>
      `${TITLE_BASE} - Search ${titleAddon(
        urlSearchParams?.get('q')
          ? new TruncatePipe().transform(urlSearchParams?.get('q'), 128)
          : undefined
      )}`,
    [`(/[^/]+)?/${SemerisRoutes.managers.root}$`]: `${TITLE_BASE} - Managers`,
    [`(/[^/]+)?/${SemerisRoutes.datapoint.root}(/[^/]+){0,1}$`]: `${TITLE_BASE} - Datapoints`,
    [`(/[^/]+)?/${SemerisRoutes.datapoint.root}/field(/[^/]+){2,}$`]: ({ field }) =>
      datapointFieldTitle(field),
    [`(/[^/]+)?/${SemerisRoutes.datapoint.root}(/[^/]+){2,}$`]: ({ rule }) => datapointTitle(rule),
    [`(/[^/]+)?/${SemerisRoutes.packages.root}/${SemerisRoutes.packages.topics}`]: `${TITLE_BASE} - Prepackaged Topics`,
    [`(/[^/]+)?/${SemerisRoutes.packages.root}/${SemerisRoutes.packages.extracts}`]: `${TITLE_BASE} - Prepackaged Extracts`,
    [`(/[^/]+)?/${SemerisRoutes.integrations.root}`]: `${TITLE_BASE} - Integrations`,
    [`(/[^/]+)?/${SemerisRoutes.gettingStarted.root}`]: `${TITLE_BASE} - Getting Started with Semeris Docs`,
    [`(/[^/]+)?/${SemerisRoutes.settings.root}`]: `${TITLE_BASE} - Profile Settings`,
    [`(/[^/]+)?/${SemerisRoutes.marketComparison.root}$`]: `${TITLE_BASE} - Topics`,
    [`(/[^/]+)?/${SemerisRoutes.topic.root}[^/]*$`]: `${TITLE_BASE} - Topics`,
    [`(/[^/]+)?/${SemerisRoutes.marketComparison.root}[?]empty=true`]: `${TITLE_BASE} - Topics - New topic`,
    [`(/[^/]+)?/${SemerisRoutes.marketComparison.root}/`]: ({ topic }) => marketCompareTitle(topic),
    [`(/[^/]+)?/${SemerisRoutes.dealFilter.root}$`]: `${TITLE_BASE} - Deal Filters`,
    [`(/[^/]+)?/${SemerisRoutes.dealFilter.root}/${SemerisRoutes.dealFilter.filter}/new.*`]: `${TITLE_BASE} - Deal Filters - New Deal Filter`,
    [`(/[^/]+)?/${SemerisRoutes.dealFilter.root}/${SemerisRoutes.dealFilter.filter}/(?!new).*`]: ({
      dealFilter,
    }) => dealFilterTitle(dealFilter),
    [`(/[^/]+)?/${SemerisRoutes.formula.root}$`]: `${TITLE_BASE} - Formulas`,
    [`(/[^/]+)?/${SemerisRoutes.formula.root}/`]: ({ formula }) => formulaTitle(formula),
    [`(/[^/]+)?/${SemerisRoutes.checklist.root}$`]: `${TITLE_BASE} - Checklists`,
    [`(/[^/]+)?/${SemerisRoutes.checklist.root}/`]: ({ checklist }) => checklistTitle(checklist),
    [`(/[^/]+)?/${SemerisRoutes.documents.root}$`]: `${TITLE_BASE} - Documents`,
    [`(/[^/]+)?/${SemerisRoutes.documents.root}/${SemerisRoutes.documents.create}$`]: `${TITLE_BASE} - Submit Documents`,
    [`(/[^/]+)?/${SemerisRoutes.transactions.root}$`]: `${TITLE_BASE} - Transactions`,
    [`(/[^/]+)?/${SemerisRoutes.transactions.root}/${SemerisRoutes.transactions.compare}`]: ({
      deals,
    }) => comparisonTitle(deals),
    [`(/[^/]+)?/${SemerisRoutes.transactions.root}(/[^/]+){0,3}$`]: ({ deal }) => dealTitle(deal),
  };

  function getTitle(parameters: TitleParameters): string {
    const key = Object.keys(simpleTitles).find((regex) => new RegExp(regex).test(parameters.url));
    if (key) {
      const handler = simpleTitles[key];

      if (typeof handler === 'string') {
        return handler;
      }

      if (typeof handler === 'function') {
        const result = handler(parameters);

        if (typeof result === 'string') {
          return result;
        }
      }
    }

    return TITLE_BASE;
  }

  function formulaTitle(formula: Formula) {
    return `${TITLE_BASE} - Formula ${titleAddon(formula?.displayName)}`;
  }

  function checklistTitle(checklist: Pick<Checklist, 'displayName'>) {
    return `${TITLE_BASE} - Checklist ${titleAddon(checklist?.displayName || 'My Topics')}`;
  }

  function comparisonTitle(deals: Transaction[]) {
    return `${TITLE_BASE} - Comparison ${titleAddon(deals?.map((d) => d.transactionDisplayName).join(', '))}`;
  }

  function marketCompareTitle(topic: Topic) {
    return `${TITLE_BASE} - Topic ${titleAddon(topic?.name)}`;
  }

  function dealTitle(deal: Transaction) {
    return `${TITLE_BASE} - Deals ${titleAddon(deal?.transactionDisplayName)}`;
  }

  function dealFilterTitle(dealFilter: DealFilter) {
    return `${TITLE_BASE} - Deals ${titleAddon(dealFilter?.displayName)}`;
  }

  function datapointTitle(rule: DatapointRule): string {
    return `${TITLE_BASE} ${titleAddon(displayNameMapper[rule?.ruleType])} ${titleAddon(rule?.displayName)}`;
  }

  function datapointFieldTitle(rule: DatapointField): string {
    return `${TITLE_BASE} ${titleAddon(displayNameMapper[DatapointRuleType.Datapoint])} ${titleAddon(rule?.displayName)}`;
  }

  function titleAddon(addon: string): string {
    return `${addon ? '- ' : ''}${addon || ''}`;
  }
}
